// Epoch 26: Dashboard using AuthenticatedLayout for global trial banner
// Replaces DashboardRefactored.tsx with cleaner implementation
// Date: August 7, 2025
// Updated: October 28, 2025 - Added Outreach Credits widget (Epoch 53)

import React, { useEffect, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { useBillingStatus } from '../hooks/useBillingStatus';
import QuotaDisplay from '../components/billing/QuotaDisplay';
import TrialInitializer from '../components/trial/TrialInitializer';
import { useTrialContext } from '../hooks/useTrialContext';
// AuthenticatedLayout is provided by AuthenticatedRoute wrapper
import { supabase } from '../supabase/client';
import TopQualifierTile from '../components/topqualifier/TopQualifierTile';
import CreditBalanceWidget from '../components/credits/CreditBalanceWidget';

const Dashboard: React.FC = () => {
  console.log('Dashboard: Component rendering with global trial banner...');
  
  const { billingStatus, loading: billingLoading } = useBillingStatus();
  const trial = useTrialContext();
  
  // Recent activity state
  interface Activity {
    id: string;
    type: 'export' | 'campaign' | 'lead_response';
    timestamp: string;
    title: string;
    description: string;
  }
  const [recentActivity, setRecentActivity] = useState<Activity[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);

  // Top Qualifier state
  const [tqEnabled, setTqEnabled] = useState<boolean>(false);
  const [tqActivatedAt, setTqActivatedAt] = useState<string | null>(null);
  const [tqLoading, setTqLoading] = useState(true);

  // Outreach Credits state (Epoch 53)
  const [usageBasedPricing, setUsageBasedPricing] = useState<boolean>(false);
  const [pricingLoading, setPricingLoading] = useState(true);

  // Epoch 84: Payment confirmation polling state
  const [paymentConfirming, setPaymentConfirming] = useState(false);
  const [paymentTimedOut, setPaymentTimedOut] = useState(false);

  // Epoch 84 Phase 2: Credits prompt for paid users with zero balance
  // null = not yet fetched (prevents flash); 0 = confirmed zero; >0 = has credits
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [creditsPromptDismissed, setCreditsPromptDismissed] = useState(
    () => localStorage.getItem('se_credits_prompt_dismissed') === 'true'
  );

  console.log('Dashboard: Trial context state:', {
    state: trial.state,
    isLoading: trial.isLoading,
    isTrialActive: trial.isTrialActive,
    daysRemaining: trial.daysRemaining
  });

  // Fetch Top Qualifier status
  useEffect(() => {
    const fetchTqStatus = async () => {
      try {
        setTqLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          setTqLoading(false);
          return;
        }

        const { data: user, error } = await supabase
          .from('users')
          .select('tq_enabled, tq_activated_at')
          .eq('id', session.user.id)
          .single();

        if (error) {
          console.error('Error fetching TQ status:', error);
        } else if (user) {
          setTqEnabled(user.tq_enabled || false);
          setTqActivatedAt(user.tq_activated_at);
        }
      } catch (error) {
        console.error('Error fetching TQ status:', error);
      } finally {
        setTqLoading(false);
      }
    };

    fetchTqStatus();
  }, []);

  // Fetch pricing plan status (Epoch 53)
  useEffect(() => {
    const fetchPricingStatus = async () => {
      try {
        setPricingLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          setPricingLoading(false);
          return;
        }

        const { data: user, error } = await supabase
          .from('users')
          .select('pricing_plan')
          .eq('id', session.user.id)
          .single();

        if (error) {
          console.error('Error fetching pricing status:', error);
        } else if (user) {
          // Show credit widget for Platform ($30/month) and usage-based (legacy) users
          setUsageBasedPricing(user.pricing_plan === 'platform' || user.pricing_plan === 'usage_based');
        }
      } catch (error) {
        console.error('Error fetching pricing status:', error);
      } finally {
        setPricingLoading(false);
      }
    };

    fetchPricingStatus();
  }, []);

  // Fetch recent activity
  useEffect(() => {
    const fetchRecentActivity = async () => {
      try {
        setActivityLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          setActivityLoading(false);
          return;
        }
        
        const activities = [];
        
        // Fetch recent lead exports (last 5)
        const { data: exports } = await supabase
          .from('lead_exports')
          .select('id, created_at, row_count, cost')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false })
          .limit(5);
        
        if (exports) {
          exports.forEach(exp => {
            activities.push({
              id: `export-${exp.id}`,
              type: 'export',
              title: 'Lead Export',
              description: `Exported ${exp.row_count} leads`,
              cost: exp.cost,
              timestamp: exp.created_at,
              icon: '📊'
            });
          });
        }
        
        // Fetch recent campaigns (last 5)
        const { data: campaigns } = await supabase
          .from('campaigns')
          .select('id, name, created_at, status')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false })
          .limit(5);
        
        if (campaigns) {
          campaigns.forEach(campaign => {
            activities.push({
              id: `campaign-${campaign.id}`,
              type: 'campaign',
              title: 'Campaign Created',
              description: campaign.name,
              status: campaign.status,
              timestamp: campaign.created_at,
              icon: '🚀'
            });
          });
        }
        
        // Fetch recent lead responses (last 5)
        const { data: responses } = await supabase
          .from('lead_responses')
          .select('id, lead_name, campaign_id, created_at, status')
          .order('created_at', { ascending: false })
          .limit(5);
        
        if (responses) {
          responses.forEach(response => {
            activities.push({
              id: `response-${response.id}`,
              type: 'lead_response',
              title: 'Lead Response',
              description: `${response.lead_name} responded`,
              timestamp: response.created_at,
              icon: '💬'
            });
          });
        }
        
        // Sort all activities by timestamp (most recent first)
        activities.sort((a, b) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
        
        // Take only the 10 most recent activities
        setRecentActivity(activities.slice(0, 10));
      } catch (error) {
        console.error('Error fetching recent activity:', error);
      } finally {
        setActivityLoading(false);
      }
    };
    
    fetchRecentActivity();
  }, []);

  // Epoch 84: Poll billing_status after Stripe payment redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') !== 'success') return;

    // Clear param from URL immediately so refresh doesn't re-trigger
    window.history.replaceState({}, '', '/dashboard');
    setPaymentConfirming(true);

    let attempts = 0;
    const maxAttempts = 5;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) { clearInterval(interval); return; }

        const { data: user } = await supabase
          .from('users')
          .select('billing_status, pricing_plan')
          .eq('id', session.user.id)
          .single();

        if (user?.billing_status === 'active' || user?.pricing_plan === 'platform') {
          clearInterval(interval);
          // Full reload so TrialProvider re-initializes with new paid_user state
          window.location.reload();
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setPaymentConfirming(false);
          setPaymentTimedOut(true);
        }
      } catch (e) {
        console.error('Payment polling error:', e);
      }
    }, 2000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Epoch 84 Phase 2: Fetch balance once paid_user state is confirmed
  useEffect(() => {
    if (!trial.isPaidUser) return;
    const fetchBalance = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-outreach-balance`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (!response.ok) return;
        const data = await response.json();
        setCreditBalance(data.balance ?? null);
      } catch (e) {
        console.error('Credit balance fetch error:', e);
      }
    };
    fetchBalance();
  }, [trial.isPaidUser]);

  const dismissCreditsPrompt = () => {
    localStorage.setItem('se_credits_prompt_dismissed', 'true');
    setCreditsPromptDismissed(true);
  };

  console.log('Dashboard: About to render JSX...');
  
  try {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Trial Initializer - COMMENTED OUT (Epoch 83: removed free trial offer, PAYG only)
        {trial.isFreeUser && !billingLoading && (
          <div className="mb-8">
            <TrialInitializer
              onTrialStarted={() => {
                console.log('Trial started! Refreshing page...');
                window.location.reload();
              }}
            />
          </div>
        )}
        */}

        {/* Epoch 84: Payment confirming spinner — shown while polling after Stripe redirect */}
        {paymentConfirming && (
          <div className="mb-8 rounded-xl bg-blue-50 border border-blue-200 p-6">
            <div className="flex items-center gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 flex-shrink-0"></div>
              <div>
                <h2 className="text-lg font-semibold text-blue-900">Confirming your payment...</h2>
                <p className="text-blue-700 text-sm mt-1">This usually takes a few seconds.</p>
              </div>
            </div>
          </div>
        )}

        {/* Epoch 84: Payment timeout fallback */}
        {paymentTimedOut && (
          <div className="mb-8 rounded-xl bg-yellow-50 border border-yellow-200 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-yellow-900">Payment received</h2>
                <p className="text-yellow-700 text-sm mt-1">Your account is being activated — refresh in a moment.</p>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium text-sm whitespace-nowrap"
              >
                Refresh now
              </button>
            </div>
          </div>
        )}

        {/* Epoch 84: Activation banner for new PAYG users who haven't subscribed yet */}
        {/* !usageBasedPricing short-circuits if the direct DB query resolves before TrialProvider */}
        {trial.isFreeUser && !usageBasedPricing && !billingLoading && !paymentConfirming && !paymentTimedOut && (
          <div className="mb-8 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold">Your account is ready</h2>
                <p className="text-blue-100 mt-1">Activate it to start your first discovery campaign.</p>
              </div>
              <a
                href="/pricing"
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-colors whitespace-nowrap"
              >
                Get Dashboard Access — $30/month →
              </a>
            </div>
          </div>
        )}

        {/* Epoch 84 Phase 2: Add Credits prompt for paid users with zero balance */}
        {trial.isPaidUser && creditBalance !== null && creditBalance === 0 && !creditsPromptDismissed && (
          <div className="mb-8 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold">You're in. Now add credits to launch your first campaign.</h2>
                <p className="text-purple-100 mt-1">Purchase outreach credits to start sending on LinkedIn.</p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <a
                  href="/credits"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-purple-700 font-semibold rounded-lg hover:bg-purple-50 transition-colors whitespace-nowrap"
                >
                  Buy Starter Credits — from $200 →
                </a>
                <button
                  onClick={dismissCreditsPrompt}
                  className="text-purple-200 hover:text-white transition-colors"
                  aria-label="Dismiss"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Welcome to Standing Encore</h1>
          <p className="text-lg text-gray-600 mt-2">Your lead generation and campaign management dashboard</p>
        </div>

        {/* Quick Stats Grid - Dynamic columns based on enabled features */}
        {/* EPOCH 64: Reorganized credit display - Shows Outreach Credits for Platform users, Trial Credits for trial users */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${
          (tqEnabled && usageBasedPricing) ? 'lg:grid-cols-5' :
          (tqEnabled || usageBasedPricing) ? 'lg:grid-cols-4' :
          'lg:grid-cols-3'
        } gap-6 mb-8`}>
          {/* Credits Card - Shows different content based on user type */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            {billingLoading ? (
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Credits</h3>
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                </div>
              </>
            ) : usageBasedPricing ? (
              // Platform/usage_based users: Show Outreach Credits widget
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">LinkedIn Outreach Credits</h3>
                <CreditBalanceWidget variant="compact" />
              </>
            ) : trial.isTrialActive ? (
              // Trial users: Show real credit balance from DB
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Trial Credits</h3>
                <CreditBalanceWidget variant="compact" />
              </>
            ) : (trial.isTrialExpired || trial.isPaidLapsed) ? (
              // Expired trial users (and lapsed offline-paid accounts): Show Outreach Credits upgrade prompt
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Outreach Credits</h3>
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Get outreach credits to power your LinkedIn campaigns.
                  </p>
                  <a
                    href="/pricing"
                    className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                  >
                    View Plans
                  </a>
                </div>
              </>
            ) : trial.isFreeUser ? (
              // Epoch 84: New PAYG users — activation CTA (hidden on mobile, banner handles it)
              <div className="hidden md:block">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Activate Your Account</h3>
                <p className="text-sm text-gray-600 mb-4">Get dashboard access to launch your first discovery campaign.</p>
                <a
                  href="/pricing"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Get Started — $30/month →
                </a>
              </div>
            ) : (
              // Legacy fallback: Show Export Credits
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Export Credits</h3>
                <QuotaDisplay variant="compact" showUpgradePrompt={true} />
              </>
            )}
          </div>

          {/* Current Plan Card */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Plan</h3>
            {billingLoading ? (
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-2/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold capitalize">
                    {trial.isTrialActive && <span className="text-purple-600">14-Day Trial</span>}
                    {(trial.isTrialExpired || trial.isPaidLapsed) && <span className="text-red-500">{trial.isPaidLapsed ? 'Subscription Expired' : 'Trial Expired'}</span>}
                    {trial.isPaidUser && <span className="text-purple-600">{billingStatus?.planType?.replace('_', ' ')}</span>}
                    {trial.isFreeUser && <span className="text-gray-400">No Active Plan</span>}
                    {trial.state.type === 'csr_user' && <span className="text-purple-600">CSR Access</span>}
                    {trial.state.type === 'error' && <span className="text-gray-400">—</span>}
                  </div>
                  {trial.isFreeUser ? (
                    <a href="/pricing" className="text-sm text-blue-600 hover:underline mt-1 inline-block">
                      Activate →
                    </a>
                  ) : !(trial.isTrialExpired || trial.isPaidLapsed) && (
                    <div className="text-sm text-gray-500 mt-1">
                      Status: <span className="capitalize text-green-600">
                        {trial.isTrialActive ? 'Active Trial' : billingStatus?.billingStatus || 'Active'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-3xl">💎</div>
              </div>
            )}
          </div>

          {/* Quick Actions Card */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <a
                href="/pricing"
                className="flex items-center justify-between p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
              >
                <span className="text-purple-700 font-medium">Purchase Credits</span>
                <span className="text-purple-500">💳</span>
              </a>
              <a
                href="/create-campaign"
                className="flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              >
                <span className="text-blue-700 font-medium">Create Campaign</span>
                <span className="text-blue-500">📋</span>
              </a>
            </div>
          </div>

          {/* EPOCH 64: Outreach Credits Widget moved to first card for Platform users */}
          {/* Keeping this commented for reference - was showing duplicate credit display */}
          {/* {!pricingLoading && usageBasedPricing && (
            <CreditBalanceWidget variant="default" />
          )} */}

          {/* Top Qualifier Tile - Only shown if enabled */}
          {!tqLoading && tqEnabled && (
            <TopQualifierTile
              tqEnabled={tqEnabled}
              tqActivatedAt={tqActivatedAt}
            />
          )}
        </div>

        {/* Campaign Performance Section - Commented out per Bug #003 */}
        {/* Will be implemented in future feature request */}
        {/*
        <div className="bg-white rounded-xl shadow-sm border mb-8">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Campaign Performance Overview</h2>
                <p className="text-gray-600 mt-1">Key metrics from your campaigns</p>
              </div>
              <a
                href="/campaign-performance"
                className="text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center"
              >
                View Full Dashboard
                <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
          
          <div className="p-6">
            <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
              <div className="text-4xl mb-4">📈</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Performance Charts Coming Soon</h3>
              <p className="text-gray-600 mb-4">
                We'll embed your campaign performance charts here once your dashboard is configured.
              </p>
              <a
                href="/campaign-performance"
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <span className="mr-2">📊</span>
                View Campaign Dashboard
              </a>
            </div>
          </div>
        </div>
        */}

        {/* Recent Activity Section */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Recent Activity</h2>
            <p className="text-gray-600 mt-1">Your latest lead exports and campaign activities</p>
          </div>
          
          <div className="p-6">
            {activityLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentActivity.length > 0 ? (
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                    <div className="text-2xl">{activity.icon}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900">{activity.title}</h4>
                        <span className="text-xs text-gray-500">
                          {new Date(activity.timestamp).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                      {activity.cost && (
                        <span className="text-xs text-green-600 font-medium mt-1 inline-block">
                          ${activity.cost.toFixed(2)}
                        </span>
                      )}
                      {activity.status && (
                        <span className={`text-xs font-medium mt-1 inline-block px-2 py-1 rounded-full ${
                          activity.status === 'active' ? 'bg-green-100 text-green-800' :
                          activity.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {activity.status}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {recentActivity.length === 10 && (
                  <div className="text-center pt-2">
                    <p className="text-sm text-gray-500">Showing 10 most recent activities</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">📁</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Recent Activity</h3>
                <p className="text-gray-600">
                  Start by exporting leads or creating campaigns to see your activity here.
                </p>
                <div className="mt-4 space-x-4">
                  <a
                    href="/lead-generator"
                    className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                  >
                    Export Leads
                  </a>
                  <a
                    href="/campaigns"
                    className="inline-flex items-center px-4 py-2 bg-white text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition-colors text-sm"
                  >
                    Create Campaign
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
  );
  
  } catch (error) {
    console.error('Dashboard: Error in component:', error);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Dashboard Error</h1>
          <p className="text-gray-600 mb-4">There was an error loading the dashboard.</p>
          <pre className="text-sm text-gray-500 bg-gray-100 p-4 rounded">
            {error?.toString()}
          </pre>
        </div>
      </div>
    );
  }
};

export default Dashboard;